/**
 * 错误码与 WS close code。见 docs/ARCHITECTURE.md §5.8 / §5.9。
 *
 * `ERROR_CODES` 是**运行时数组**（网关层 zod schema 与测试都要枚举），
 * `ErrorCode` 由它派生，所以两者永远不会漂移——加一个码只需改一处。
 */

/**
 * 全部错误码。分组顺序与 §5.8 一致。
 *
 * 命名约定：`E_` + 大写下划线。名字描述**原因**而不是 HTTP 状态，
 * 因为同一个码在 WS 与 HTTP 两条通道上都要用。
 */
export const ERROR_CODES = [
  // —— 连接 / 协议 ——
  'E_PROTOCOL_VERSION',
  'E_MALFORMED',
  'E_UNKNOWN_MESSAGE',
  'E_PAYLOAD_TOO_LARGE',
  'E_RATE_LIMITED',
  'E_NOT_AUTHENTICATED',
  'E_AUTH_INVALID',
  'E_AUTH_EXPIRED',
  'E_AUTH_REPLAY',
  'E_RESUME_EXPIRED',
  'E_SESSION_SUPERSEDED',
  'E_EPOCH_MISMATCH',
  /** 邮箱登录：这个邮箱已经绑在另一个账号上（credentials 的唯一索引撞键） */
  'E_EMAIL_TAKEN',
  /**
   * 绑邮箱的验证码不对、过期了，或者改绑时旧密码不对 —— 四种失败同一个码。
   * ★ 单开一个码而不是复用 E_AUTH_INVALID：客户端把后者在绑定语境下翻成
   *   「登录状态过期了，刷新一下页面」，用户一刷新面板就卸载，收件箱里那枚
   *   还有效的码就失去了输入口。
   */
  'E_VERIFY_CODE_INVALID',
  /** 这个客户端的 bundle 太旧，用的端点已经下线了。装在桌面上的 PWA 能无限期跑旧代码 */
  'E_CLIENT_OUTDATED',
  // —— 房间 ——
  'E_ROOM_NOT_FOUND',
  'E_ROOM_CODE_INVALID',
  'E_ROOM_FULL',
  'E_ROOM_CLOSED',
  'E_ALREADY_IN_ROOM',
  'E_NOT_IN_ROOM',
  'E_ROOM_LIMIT',
  'E_BANNED',
  // —— 权限 ——
  'E_NOT_HOST',
  'E_CANNOT_KICK_SELF',
  'E_CONFIG_LOCKED',
  'E_SEATS_OCCUPIED',
  'E_BOT_LIMIT',
  // —— 座位 ——
  'E_SEAT_INVALID',
  'E_SEAT_TAKEN',
  'E_SEAT_RESERVED',
  'E_ALREADY_SEATED',
  'E_NOT_SEATED',
  'E_SEAT_NOT_YOURS',
  'E_CANNOT_STAND_MIDHAND',
  /** 换座：本手发到了牌（弃了也算），这手打完再换 */
  'E_SEAT_CHANGE_MIDHAND',
  'E_REBUY_NOT_ALLOWED',
  // —— 买分（top-up）——
  /** 已达起始筹码线 / 已有一笔待生效的补分 / 牌局已结算 */
  'E_TOPUP_NOT_ALLOWED',
  /** 申请不存在、已被处理，或不属于你（不区分，避免探测别人的申请 id） */
  'E_TOPUP_NOT_FOUND',
  // —— 玩家间转账 ——
  /** 不能转给自己 / 出让方筹码不够 / 受让方已达起始线 / 牌局已结算 */
  'E_TRANSFER_NOT_ALLOWED',
  /** 申请不存在、已被处理，或轮不到你处理（不区分，避免探测别人的申请 id） */
  'E_TRANSFER_NOT_FOUND',
  // —— 行动 ——
  'E_NOT_ACTOR',
  'E_HAND_NOT_RUNNING',
  'E_HAND_MISMATCH',
  'E_NOT_YOUR_TURN',
  'E_STALE_ACTION',
  'E_FUTURE_ACTION',
  'E_ILLEGAL_ACTION',
  'E_BET_TOO_SMALL',
  'E_BET_TOO_LARGE',
  'E_BET_NOT_INTEGER',
  'E_ACTION_NOT_REOPENED',
  'E_NO_TIME_BANK',
  // —— 流程 ——
  'E_GAME_ALREADY_RUNNING',
  'E_NOT_ENOUGH_PLAYERS',
  'E_INTERNAL',
  // —— 头像 ——
  /** 请求的头像文件不存在（账号没上传过 / 已经改回默认头像） */
  'E_AVATAR_NOT_FOUND',
  /** 上传的文件不是图片、格式不在白名单，或超过大小上限 */
  'E_AVATAR_INVALID',
] as const

export type ErrorCode = (typeof ERROR_CODES)[number]

/**
 * ★ `retryable === true` **仅**用于这两个码（§5.8）。
 *
 * 其余错误客户端一律**不重发**：只回滚乐观 UI + 请求快照。
 * 让 `E_ILLEGAL_ACTION` 之类可重试会造成「服务端已经推进到下一个动作、
 * 客户端还在重发上一个」的循环，而 `actionToken` 一次性使用会让每次重发都失败。
 */
export const RETRYABLE_ERROR_CODES: readonly ErrorCode[] = ['E_RATE_LIMITED', 'E_INTERNAL']

export function isRetryableErrorCode(code: ErrorCode): boolean {
  return RETRYABLE_ERROR_CODES.includes(code)
}

export function isErrorCode(s: string): s is ErrorCode {
  return (ERROR_CODES as readonly string[]).includes(s)
}

export interface ErrorPayload {
  readonly code: ErrorCode
  /** 面向用户的中文短句；细节放 details，不要把内部栈信息拼进来 */
  readonly message: string
  /** 必须与 `isRetryableErrorCode(code)` 一致 */
  readonly retryable: boolean
  readonly retryAfterMs?: number
  /** 触发本错误的 `ClientFrame.cid`，客户端据此定位是哪条消息失败 */
  readonly refCid?: number
  /** 触发本错误的 `playerAction.clientActionId`，用于精确回滚某一次乐观 UI */
  readonly refClientActionId?: string
  /** 动作类错误专用：这一步实际是被谁执行掉的 */
  readonly appliedBy?: 'you' | 'otherDevice' | 'timeout'
  readonly details?: Readonly<Record<string, unknown>>
}

/**
 * WS close code 表（§5.9）。
 *
 * ★ 因限速关连接时**必须先发 `error` 帧再 close(4429)**：close 帧的 reason
 *   只有 123 字节，且浏览器不保证把它交给 onclose 回调。
 */
export const WS_CLOSE = {
  /** 客户端主动判死（心跳超时）。客户端行为：立即重连，不退避 */
  CLIENT_DEAD: 4000,
  /** 协议错误。退避重连；连续 3 次转 fatal */
  PROTOCOL_ERROR: 4400,
  /** 鉴权失败 / 被撤销。尝试一次 /refresh；失败转 fatal 回登录页 */
  AUTH_FAILED: 4401,
  /** 协议版本不兼容。提示刷新；reload 带频率保护（10 分钟至多 1 次） */
  VERSION_MISMATCH: 4402,
  /** 连接数超限（> WS.MAX_CONNECTIONS_PER_ACCOUNT）。退避重连 */
  TOO_MANY_CONNECTIONS: 4008,
  /** 被新连接取代（singleConnectionPerAccount）。★ 不重连 */
  SESSION_SUPERSEDED: 4009,
  /** 限速。按 retryAfterMs 退避 */
  RATE_LIMITED: 4429,
  /** 页面卸载（由客户端发出） */
  PAGE_UNLOAD: 1001,
} as const

export type WsCloseCode = (typeof WS_CLOSE)[keyof typeof WS_CLOSE]

/** 客户端收到该 close code 后应当采取的行为（§5.9 表的第三列）。 */
export type WsCloseBehavior =
  | 'reconnectNow'
  | 'reconnectBackoff'
  | 'refreshAuth'
  | 'promptReload'
  | 'stop'

export const WS_CLOSE_BEHAVIOR: Readonly<Record<WsCloseCode, WsCloseBehavior>> = {
  [WS_CLOSE.CLIENT_DEAD]: 'reconnectNow',
  [WS_CLOSE.PROTOCOL_ERROR]: 'reconnectBackoff',
  [WS_CLOSE.AUTH_FAILED]: 'refreshAuth',
  [WS_CLOSE.VERSION_MISMATCH]: 'promptReload',
  [WS_CLOSE.TOO_MANY_CONNECTIONS]: 'reconnectBackoff',
  /** ★ 唯一「不重连」的分支：重连只会把另一台设备再踢下线，两台设备无限对踢 */
  [WS_CLOSE.SESSION_SUPERSEDED]: 'stop',
  [WS_CLOSE.RATE_LIMITED]: 'reconnectBackoff',
  [WS_CLOSE.PAGE_UNLOAD]: 'stop',
}

export function closeBehaviorOf(code: number): WsCloseBehavior {
  return WS_CLOSE_BEHAVIOR[code as WsCloseCode] ?? 'reconnectBackoff'
}
